/**
 * Local cache for symbol-bundle data.
 * Pattern: stale-while-revalidate — return cached data instantly, then refresh in background.
 *
 * Key: `sbundle:${SYMBOL}:${opts}`
 * TTLs:
 *   - Profile/news section data stays useful for 10 min
 *   - Quote data is refreshed on every visit (but cached profile displays instantly)
 *   - Hard expiry at 30 min — older data gets wiped on read
 */

const PREFIX = "omega_sbundle_v1:";
const HARD_EXPIRY_MS = 30 * 60 * 1000; // 30 min
const FRESH_THRESHOLD_MS = 60 * 1000;  // under 60s = fresh, don't even revalidate quote

export function cacheKey(symbol, opts = {}) {
  const parts = [symbol.toUpperCase()];
  if (opts.include_chain) parts.push("chain");
  if (opts.news_limit != null) parts.push(`n${opts.news_limit}`);
  return PREFIX + parts.join(":");
}

export function getCached(symbol, opts = {}) {
  try {
    const raw = localStorage.getItem(cacheKey(symbol, opts));
    if (!raw) return null;
    const entry = JSON.parse(raw);
    const age = Date.now() - (entry.ts || 0);
    if (age > HARD_EXPIRY_MS) {
      localStorage.removeItem(cacheKey(symbol, opts));
      return null;
    }
    entry.__age = age;
    entry.__isFresh = age < FRESH_THRESHOLD_MS;
    return entry;
  } catch {
    return null;
  }
}

export function setCached(symbol, opts = {}, data) {
  try {
    const payload = { ...data, ts: Date.now() };
    localStorage.setItem(cacheKey(symbol, opts), JSON.stringify(payload));
  } catch (e) {
    // Quota exceeded — clear old entries and retry once
    try {
      clearOldest(20);
      const payload = { ...data, ts: Date.now() };
      localStorage.setItem(cacheKey(symbol, opts), JSON.stringify(payload));
    } catch {}
  }
}

// Evict oldest N entries to free quota
function clearOldest(n = 10) {
  const entries = [];
  for (let i = 0; i < localStorage.length; i++) {
    const k = localStorage.key(i);
    if (!k || !k.startsWith(PREFIX)) continue;
    try {
      const e = JSON.parse(localStorage.getItem(k));
      entries.push([k, e.ts || 0]);
    } catch {
      localStorage.removeItem(k);
    }
  }
  entries.sort((a, b) => a[1] - b[1]);
  entries.slice(0, n).forEach(([k]) => localStorage.removeItem(k));
}

// List cached symbols — useful for Recent/Favorites surfaces
export function listCachedSymbols() {
  const out = [];
  for (let i = 0; i < localStorage.length; i++) {
    const k = localStorage.key(i);
    if (!k || !k.startsWith(PREFIX)) continue;
    const sym = k.slice(PREFIX.length).split(":")[0];
    if (sym && !out.includes(sym)) out.push(sym);
  }
  return out;
}

export function clearSymbolCache() {
  const keys = [];
  for (let i = 0; i < localStorage.length; i++) {
    const k = localStorage.key(i);
    if (k && k.startsWith(PREFIX)) keys.push(k);
  }
  keys.forEach(k => localStorage.removeItem(k));
}

// Recent tickers tracker (for "jump back" UI)
const RECENT_KEY = "omega_recent_tickers_v1";
const RECENT_MAX = 12;

// ─── Option chain cache ──────────────────────────────────────────────────────
// Separate prefix + tighter TTL (chain strikes move with the market).
const CHAIN_PREFIX = "omega_chain_v1:";
const CHAIN_HARD_EXPIRY_MS = 5 * 60 * 1000; // 5 min hard expiry
const CHAIN_FRESH_MS = 15 * 1000; // < 15s = fresh, don't revalidate

function chainKey(symbol, expiry = "") {
  return CHAIN_PREFIX + symbol.toUpperCase() + ":" + (expiry || "nearest");
}

export function getCachedChain(symbol, expiry = "") {
  try {
    const raw = localStorage.getItem(chainKey(symbol, expiry));
    if (!raw) return null;
    const entry = JSON.parse(raw);
    const age = Date.now() - (entry.ts || 0);
    if (age > CHAIN_HARD_EXPIRY_MS) {
      localStorage.removeItem(chainKey(symbol, expiry));
      return null;
    }
    entry.__age = age;
    entry.__isFresh = age < CHAIN_FRESH_MS;
    return entry;
  } catch {
    return null;
  }
}

export function setCachedChain(symbol, expiry = "", payload) {
  try {
    localStorage.setItem(chainKey(symbol, expiry), JSON.stringify({ ...payload, ts: Date.now() }));
  } catch {
    try {
      // Clear oldest chain entries only
      const keys = [];
      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i);
        if (k && k.startsWith(CHAIN_PREFIX)) keys.push(k);
      }
      keys.slice(0, 10).forEach(k => localStorage.removeItem(k));
      localStorage.setItem(chainKey(symbol, expiry), JSON.stringify({ ...payload, ts: Date.now() }));
    } catch {}
  }
}

// Expirations list (rarely changes intraday)
const EXP_PREFIX = "omega_exp_v1:";
const EXP_EXPIRY_MS = 30 * 60 * 1000; // 30 min

export function getCachedExpirations(symbol) {
  try {
    const raw = localStorage.getItem(EXP_PREFIX + symbol.toUpperCase());
    if (!raw) return null;
    const entry = JSON.parse(raw);
    if (Date.now() - (entry.ts || 0) > EXP_EXPIRY_MS) {
      localStorage.removeItem(EXP_PREFIX + symbol.toUpperCase());
      return null;
    }
    return entry.expirations;
  } catch {
    return null;
  }
}

export function setCachedExpirations(symbol, expirations) {
  try {
    localStorage.setItem(EXP_PREFIX + symbol.toUpperCase(), JSON.stringify({ expirations, ts: Date.now() }));
  } catch {}
}

export function addRecentTicker(symbol) {
  if (!symbol) return;
  try {
    const cur = JSON.parse(localStorage.getItem(RECENT_KEY) || "[]");
    const filtered = cur.filter(s => s !== symbol);
    filtered.unshift(symbol);
    localStorage.setItem(RECENT_KEY, JSON.stringify(filtered.slice(0, RECENT_MAX)));
  } catch {}
}

export function getRecentTickers() {
  try {
    return JSON.parse(localStorage.getItem(RECENT_KEY) || "[]");
  } catch {
    return [];
  }
}

// ─── Favorites (user-pinned tickers) ─────────────────────────────────────────
const FAVORITES_KEY = "omega_favorites_v1";

export function getFavorites() {
  try {
    return JSON.parse(localStorage.getItem(FAVORITES_KEY) || "[]");
  } catch {
    return [];
  }
}

// Sync favorites to backend (non-blocking, best-effort)
async function syncFavoritesToBackend(list) {
  try {
    const axios = (await import("axios")).default;
    const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
    await axios.post(`${API}/favorites/sync`, { symbols: list });
  } catch {}
}

// Pull favorites from backend (for cross-device continuity). Merges into local.
export async function loadFavoritesFromBackend() {
  try {
    const axios = (await import("axios")).default;
    const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
    const r = await axios.get(`${API}/favorites`);
    const remote = r.data?.symbols || [];
    if (remote.length === 0) return;
    const local = getFavorites();
    // Union merge: preserve local order first, then append any remote symbols
    // not already in local. This gives cross-device continuity without losing
    // either device's additions.
    const merged = [...local];
    for (const sym of remote) {
      if (!merged.includes(sym)) merged.push(sym);
    }
    const capped = merged.slice(0, 20);
    localStorage.setItem(FAVORITES_KEY, JSON.stringify(capped));
    // Push merged list back so backend stays in sync
    if (merged.length !== local.length) {
      syncFavoritesToBackend(capped);
    }
  } catch {}
}

export function toggleFavorite(symbol) {
  if (!symbol) return [];
  try {
    const cur = getFavorites();
    const isFav = cur.includes(symbol);
    const next = isFav ? cur.filter(s => s !== symbol) : [symbol, ...cur].slice(0, 20);
    localStorage.setItem(FAVORITES_KEY, JSON.stringify(next));
    // Fire-and-forget backend sync so pre-warmer picks it up within 5 min
    syncFavoritesToBackend(next);
    return next;
  } catch {
    return [];
  }
}

export function isFavorite(symbol) {
  return getFavorites().includes(symbol);
}
